export type ActivitySource = string;

export interface ActivityTransition {
  activityId: string;
  otherActivityId: string;
}

export interface ActivityDefinition {
  id: string;
  name: string;
  icon: string;
  order: number;
  description?: string;
  instrument?: boolean;
  listed?: boolean;
  activate?: (transition: ActivityTransition) => void;
  deactivate?: (transition: ActivityTransition) => void;
  onNotesChanged?: (source: ActivitySource) => void;
}

export interface ActivityRegistry {
  register(activity: ActivityDefinition): () => void;
  get(id: string): Readonly<ActivityDefinition> | undefined;
  list(): ReadonlyArray<Readonly<ActivityDefinition>>;
  transition(previousId: string, nextId: string): void;
  notesChanged(activityId: string, source: ActivitySource): boolean;
}

export function createActivityRegistry(): ActivityRegistry {
  const activities = new Map<string, Readonly<ActivityDefinition>>();

  return Object.freeze({
    register(activity: ActivityDefinition) {
      if (!activity.id) throw new Error('Activity id is required');
      if (activities.has(activity.id)) throw new Error(`Activity already registered: ${activity.id}`);
      const registered = Object.freeze({ ...activity });
      activities.set(registered.id, registered);
      return () => activities.delete(registered.id);
    },

    get(id: string) {
      return activities.get(id);
    },

    list() {
      return [...activities.values()].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    },

    transition(previousId: string, nextId: string) {
      if (previousId !== nextId) {
        activities.get(previousId)?.deactivate?.({ activityId: previousId, otherActivityId: nextId });
      }
      activities.get(nextId)?.activate?.({ activityId: nextId, otherActivityId: previousId });
    },

    notesChanged(activityId: string, source: ActivitySource) {
      const handler = activities.get(activityId)?.onNotesChanged;
      if (!handler) return false;
      handler(source);
      return true;
    },
  });
}
